import { useState, useEffect } from "react";
import { api, API, eur } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MAR_ACCOUNTS, marName } from "@/lib/mar";
import { BookOpenCheck, CheckCircle2, Clock, Layers, Download, FileSpreadsheet, Eye, ExternalLink, Loader2, Pencil, Trash2, Search } from "lucide-react";

const SOURCE_LABEL = { viva: "Viva Wallet", bnp: "BNP Paribas", factuurboek: "Factuurboek", dagomzetten: "Dagontvangsten", overboeking: "Viva ↔ BNP", lening: "Lening", kasboek: "Kasboek", afschrijving: "Afschrijving" };
const SOURCE_COLOR = { viva: "text-emerald-700 border-emerald-300", bnp: "text-slate-700 border-slate-300", factuurboek: "text-indigo-700 border-indigo-300", dagomzetten: "text-amber-700 border-amber-300", overboeking: "text-cyan-700 border-cyan-300", lening: "text-rose-700 border-rose-300", kasboek: "text-lime-700 border-lime-300", afschrijving: "text-violet-700 border-violet-300" };
const SOURCE_ORIGIN = { factuurboek: "Vanuit factuurboek", dagomzetten: "Automatisch · dagontvangsten", overboeking: "Automatisch · overboeking", lening: "Automatisch · jaareinde", kasboek: "Automatisch · kasboek", afschrijving: "Automatisch · vaste activa" };

export default function Grootboek() {
  const [tab, setTab] = useState("pending");
  const [start, setStart] = useState("2026-01-01");
  const [end, setEnd] = useState("2026-12-31");
  const [data, setData] = useState({ entries: [], pending_count: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [dagPerShop, setDagPerShop] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const filtered = data.entries.filter((e) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const hay = `${e.description || ""} ${SOURCE_LABEL[e.source] || e.source || ""} ${e.volgnummer || ""} ${e.bewijsstuk || ""} ${e.mededeling || ""} ${e.date || ""} ${e.total_debet || ""} ${e.total_credit || ""} ${(e.lines || []).map((l) => `${l.account} ${l.name} ${l.debet || ""} ${l.credit || ""}`).join(" ")}`.toLowerCase();
    return hay.includes(q);
  });

  const exportLedger = async (format) => {
    setExporting(true);
    try {
      const r = await api.get("/ledger/export", { params: { status: tab, from_date: start, to_date: end, dag_per_shop: dagPerShop, format, pub: process.env.REACT_APP_BACKEND_URL }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `Grootboek_${start}_${end}.${format === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export mislukt"); }
    finally { setExporting(false); }
  };

  const load = () => {
    setLoading(true);
    api.get("/ledger/entries", { params: { status: tab, from_date: start, to_date: end, dag_per_shop: dagPerShop } })
      .then((r) => setData(r.data)).catch(() => setData({ entries: [], pending_count: 0 }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, start, end, dagPerShop]);
  useEffect(() => { setSelected(new Set()); }, [tab, start, end, dagPerShop, search]);

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allVisibleSelected = filtered.length > 0 && filtered.every((e) => selected.has(e.id));
  const toggleSelectAll = () => setSelected((prev) => {
    if (filtered.every((e) => prev.has(e.id))) return new Set();
    return new Set(filtered.map((e) => e.id));
  });
  const bulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`${ids.length} geselecteerde boeking(en) uit het grootboek verwijderen?\n\nBank/provider-boekingen keren terug naar 'nog te bevestigen' in Bank & Betaalproviders.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/ledger/entries/bulk-delete", { ids });
      const parts = [];
      if (r.data.deleted) parts.push(`${r.data.deleted} verwijderd`);
      if (r.data.returned_to_provider) parts.push(`${r.data.returned_to_provider} teruggezet naar providers`);
      toast.success(parts.join(" · ") || "Klaar");
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verwijderen mislukt"); }
    finally { setBulkBusy(false); }
  };

  const confirm = async (id) => {
    setBusyId(id);
    try { await api.post(`/ledger/entries/${id}/confirm`); toast.success("Boeking bevestigd"); load(); }
    catch (e) { toast.error("Bevestigen mislukt"); }
    finally { setBusyId(""); }
  };

  const [detail, setDetail] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const openDetail = async (e) => {
    setDetail(e);
    setEditLines(null);
    setPreviewUrl("");
    if (e.doc_id) {
      setPreviewLoading(true);
      try {
        const token = localStorage.getItem("token");
        const url = e.volgnummer
          ? `${API}/documents/${e.doc_id}/page?q=${encodeURIComponent(e.volgnummer)}`
          : `${API}/documents/${e.doc_id}/preview`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setPreviewUrl(URL.createObjectURL(await res.blob()));
      } catch { /* stil */ }
      finally { setPreviewLoading(false); }
    }
  };
  const openDocFile = async (docId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/documents/${docId}/file`, { headers: { Authorization: `Bearer ${token}` } });
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch { toast.error("Kon document niet openen"); }
  };
  const confirmFromDetail = async () => {
    if (!detail) return;
    await confirm(detail.id);
    setDetail(null);
  };

  const [editLines, setEditLines] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const startEdit = () => setEditLines(detail.lines.map((l) => ({ ...l })));
  const cancelEdit = () => setEditLines(null);
  const setEditLine = (i, f, v) => setEditLines((rows) => {
    const r = rows.map((l) => ({ ...l }));
    r[i][f] = v;
    if (f === "account") { const nm = marName(v); if (nm) r[i].name = nm; }
    if (f === "debet" && (parseFloat(v) || 0) !== 0) r[i].credit = 0;
    if (f === "credit" && (parseFloat(v) || 0) !== 0) r[i].debet = 0;
    return r;
  });
  const addEditLine = () => setEditLines((r) => [...r, { account: "", name: "", debet: 0, credit: 0 }]);
  const removeEditLine = (i) => setEditLines((r) => r.filter((_, idx) => idx !== i));
  const editDeb = editLines ? Math.round(editLines.reduce((s, l) => s + (parseFloat(l.debet) || 0), 0) * 100) / 100 : 0;
  const editCred = editLines ? Math.round(editLines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0) * 100) / 100 : 0;
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = editLines.map((l) => ({ account: (l.account || "").toString().trim(), name: (l.name || "").toString().trim(), debet: parseFloat(l.debet) || 0, credit: parseFloat(l.credit) || 0 }));
      const r = await api.patch(`/ledger/entries/${detail.id}/edit`, { lines: payload });
      setDetail((d) => ({ ...d, lines: r.data.lines, total_debet: r.data.total_debet, total_credit: r.data.total_credit, edited: true }));
      setEditLines(null);
      toast.success("Boeking aangepast");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSavingEdit(false); }
  };
  const deleteEntry = async () => {
    if (!window.confirm("Deze boeking uit het grootboek verwijderen?")) return;
    try {
      const r = await api.delete(`/ledger/entries/${detail.id}`);
      toast.success(r.data.returned_to_provider ? "Boeking teruggezet naar Bank & Betaalproviders" : "Boeking verwijderd");
      setDetail(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verwijderen mislukt"); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="grootboek-page">
      <datalist id="mar-accounts">
        {MAR_ACCOUNTS.map((a) => <option key={a.code} value={a.code} label={`${a.code} · ${a.name}`} />)}
      </datalist>
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><BookOpenCheck className="h-6 w-6 text-primary" /> Grootboek</h1>
        <p className="text-sm text-muted-foreground mt-1">Alle grootboekboekingen: eerst controleren en bevestigen, daarna staan ze bij de bevestigde boekingen.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="grootboek-tabs">
            <TabsTrigger value="pending" data-testid="tab-pending"><Clock className="h-4 w-4 mr-1.5" /> Nog te bevestigen {data.pending_count > 0 && <Badge className="ml-2 bg-amber-500">{data.pending_count}</Badge>}</TabsTrigger>
            <TabsTrigger value="confirmed" data-testid="tab-confirmed"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Bevestigd</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all"><Layers className="h-4 w-4 mr-1.5" /> Alle</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2 text-sm">
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="grootboek-start" />
          <span className="text-muted-foreground">→</span>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="grootboek-end" />
        </div>
        <div className="flex items-center gap-3 ml-auto">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="dag-per-shop-label">
            <input type="checkbox" className="h-4 w-4" data-testid="dag-per-shop" checked={dagPerShop} onChange={(e) => setDagPerShop(e.target.checked)} />
            <span>Dagomzetten per vestiging</span>
          </label>
          <Button variant="outline" size="sm" data-testid="export-grootboek-pdf" onClick={() => exportLedger("pdf")} disabled={exporting}><Download className="h-4 w-4 mr-1.5" /> PDF</Button>
          <Button variant="outline" size="sm" data-testid="export-grootboek-excel" onClick={() => exportLedger("xlsx")} disabled={exporting}><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel</Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoek op omschrijving, bron, volgnummer, rekening, bedrag, mededeling…" className="h-9" data-testid="grootboek-search" />
        {search && <Button variant="ghost" size="sm" className="h-9 shrink-0" data-testid="grootboek-search-clear" onClick={() => setSearch("")}>Wissen</Button>}
      </div>

      {!loading && filtered.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2" data-testid="grootboek-bulk-bar">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAll} data-testid="grootboek-select-all" />
            <span>{selected.size > 0 ? `${selected.size} geselecteerd` : "Alles selecteren"}</span>
          </label>
          {selected.size > 0 && (
            <Button variant="outline" size="sm" className="ml-auto text-red-600 border-red-300 hover:bg-red-50"
              disabled={bulkBusy} onClick={bulkDelete} data-testid="grootboek-bulk-delete">
              <Trash2 className="h-4 w-4 mr-1.5" /> {bulkBusy ? "Bezig…" : `Verwijder ${selected.size}`}
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center text-muted-foreground py-12">Laden…</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">{search ? "Geen boekingen gevonden voor deze zoekopdracht." : "Geen boekingen in deze selectie."}</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {filtered.map((e) => (
            <Card key={e.id} className={`shadow-sm ${selected.has(e.id) ? "ring-2 ring-red-300" : ""}`} data-testid={`ledger-entry-${e.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Checkbox className="mt-1" checked={selected.has(e.id)} onCheckedChange={() => toggleSelect(e.id)} data-testid={`ledger-select-${e.id}`} onClick={(ev) => ev.stopPropagation()} />
                    <div>
                      <CardTitle className="font-heading text-base">{e.description}</CardTitle>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        <Badge variant="outline" className={SOURCE_COLOR[e.source] || ""}>{SOURCE_LABEL[e.source] || e.source}</Badge>
                        <span className="text-xs text-muted-foreground">{e.date}</span>
                        {e.volgnummer && <Badge className="bg-amber-100 text-amber-900 border border-amber-300 font-mono" data-testid={`volgnr-${e.id}`}>Volgnr. {e.volgnummer}</Badge>}
                        {e.tx_count > 0 && <span className="text-xs text-muted-foreground">· {e.tx_count} transacties</span>}
                        {e.reconciled === true && <Badge variant="outline" className="text-emerald-700 border-emerald-300" data-testid={`recon-ok-${e.id}`}>Dubbele controle ✓</Badge>}
                        {e.reconciled === false && <Badge variant="outline" className="text-red-700 border-red-300" data-testid={`recon-nok-${e.id}`}>Niet in balans</Badge>}
                      </div>
                    </div>
                  </div>
                  {e.status === "confirmed"
                    ? <Badge className="bg-emerald-600 gap-1 shrink-0"><CheckCircle2 className="h-3.5 w-3.5" /> Bevestigd</Badge>
                    : e.confirmable
                      ? <Button size="sm" disabled={busyId === e.id} onClick={() => confirm(e.id)} data-testid={`confirm-${e.id}`}>{busyId === e.id ? "Bezig…" : "Bevestigen"}</Button>
                      : <Badge variant="secondary" className="shrink-0">{SOURCE_ORIGIN[e.source] || "Automatisch"}</Badge>}
                </div>
              </CardHeader>
              <CardContent>
                <button type="button" onClick={() => openDetail(e)}
                  className="w-full text-left rounded-lg border border-indigo-200 bg-indigo-50/60 hover:bg-indigo-100/70 transition-colors px-3 py-2 mb-3 group" data-testid={`verantwoordingsstuk-${e.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">Verantwoordingsstuk</div>
                    <span className="text-[11px] text-indigo-600 flex items-center gap-1 opacity-80 group-hover:opacity-100"><Eye className="h-3.5 w-3.5" /> Bekijk</span>
                  </div>
                  <div className="text-sm text-indigo-950 font-medium flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span>{e.bewijsstuk || "—"}</span>
                    <span className="text-indigo-700">· {e.date}</span>
                  </div>
                  {e.mededeling && <div className="text-xs text-indigo-800/90 mt-0.5">Mededeling: {e.mededeling}</div>}
                </button>
                <div className="rounded-lg border overflow-hidden">
                  <div className="grid grid-cols-[70px_1fr_110px_110px] gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/50">
                    <span>Rekening</span><span>Omschrijving</span><span className="text-right">Debet</span><span className="text-right">Credit</span>
                  </div>
                  {e.lines.map((l, i) => (
                    <div key={i} className="grid grid-cols-[70px_1fr_110px_110px] gap-2 px-3 py-1.5 text-sm border-t items-center">
                      <span className="font-mono text-xs">{l.account}</span>
                      <span className="truncate">{l.name}</span>
                      <span className="text-right tabular-nums">{l.debet ? eur(l.debet) : ""}</span>
                      <span className="text-right tabular-nums">{l.credit ? eur(l.credit) : ""}</span>
                    </div>
                  ))}
                  <div className="grid grid-cols-[70px_1fr_110px_110px] gap-2 px-3 py-1.5 text-sm font-semibold border-t bg-muted/30">
                    <span></span><span>Totaal</span>
                    <span className="text-right tabular-nums">{eur(e.total_debet)}</span>
                    <span className="text-right tabular-nums">{eur(e.total_credit)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ledger-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-base">{detail.description}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={SOURCE_COLOR[detail.source] || ""}>{SOURCE_LABEL[detail.source] || detail.source}</Badge>
                  <span className="text-xs text-muted-foreground">{detail.date}</span>
                  {detail.volgnummer && <Badge className="bg-amber-100 text-amber-900 border border-amber-300 font-mono" data-testid="detail-volgnr">Volgnr. {detail.volgnummer}</Badge>}
                </div>
                <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">Verantwoordingsstuk</div>
                  <div className="text-sm text-indigo-950 font-medium">{detail.bewijsstuk || "—"} · {detail.date}</div>
                  {detail.mededeling && <div className="text-xs text-indigo-800/90 mt-0.5">Mededeling: {detail.mededeling}</div>}
                </div>

                {detail.doc_id ? (
                  <div className="rounded-lg border overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b">
                      <span className="text-xs font-semibold text-muted-foreground">{detail.volgnummer ? "Bewijsstuk · pagina met gemarkeerd volgnummer" : "Origineel document"}</span>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="detail-open-doc" onClick={() => openDocFile(detail.doc_id)}>
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Openen
                      </Button>
                    </div>
                    <div className="max-h-[45vh] overflow-y-auto bg-neutral-50 flex justify-center">
                      {previewLoading ? (
                        <div className="py-10 text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Document laden…</div>
                      ) : previewUrl ? (
                        <img src={previewUrl} alt="document" className="w-full object-contain" data-testid="detail-preview-img" />
                      ) : (
                        <div className="py-10 text-sm text-muted-foreground">Geen voorbeeld beschikbaar. Gebruik "Openen".</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Geen gekoppeld brondocument — dit is een automatische/afgeleide boeking. Controleer de boekingsregels hieronder.</p>
                )}

                <div className="rounded-lg border overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b">
                    <span className="text-xs font-semibold text-muted-foreground">Boeking {detail.edited && !editLines ? "· aangepast" : ""}</span>
                    {!editLines && <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="detail-edit-start" onClick={startEdit}><Pencil className="h-3.5 w-3.5 mr-1" /> Aanpassen</Button>}
                  </div>
                  {editLines ? (
                    <div className="p-2 space-y-1.5" data-testid="detail-edit">
                      <div className="grid grid-cols-[72px_1fr_84px_84px_24px] gap-1.5 text-[11px] text-muted-foreground px-0.5">
                        <span>Rekening</span><span>Omschrijving</span><span className="text-right">Debet</span><span className="text-right">Credit</span><span></span>
                      </div>
                      {editLines.map((l, i) => (
                        <div key={i} className="grid grid-cols-[72px_1fr_84px_84px_24px] gap-1.5 items-center">
                          <Input className="h-8 font-mono text-xs px-1.5" list="mar-accounts" placeholder="bv. 604006" data-testid={`edit-acct-${i}`} value={l.account} onChange={(ev) => setEditLine(i, "account", ev.target.value)} />
                          <Input className="h-8 text-xs px-1.5" value={l.name} onChange={(ev) => setEditLine(i, "name", ev.target.value)} />
                          <Input type="number" step="0.01" className="h-8 text-xs px-1.5 text-right" value={l.debet || ""} onChange={(ev) => setEditLine(i, "debet", ev.target.value)} />
                          <Input type="number" step="0.01" className="h-8 text-xs px-1.5 text-right" value={l.credit || ""} onChange={(ev) => setEditLine(i, "credit", ev.target.value)} />
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeEditLine(i)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                        </div>
                      ))}
                      <div className="grid grid-cols-[72px_1fr_84px_84px_24px] gap-1.5 text-sm font-semibold border-t pt-1">
                        <span></span><span>Totaal</span>
                        <span className="text-right tabular-nums">{eur(editDeb)}</span>
                        <span className="text-right tabular-nums">{eur(editCred)}</span><span></span>
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addEditLine}>+ Regel</Button>
                        <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 ml-auto" disabled={editDeb !== editCred || savingEdit} data-testid="detail-edit-save" onClick={saveEdit}>{savingEdit ? "Bezig…" : "Opslaan"}</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={cancelEdit}>Annuleren</Button>
                      </div>
                      {editDeb !== editCred && <p className="text-[11px] text-red-600">Debet en credit moeten gelijk zijn.</p>}
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-[70px_1fr_110px_110px] gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/30 border-b">
                        <span>Rekening</span><span>Omschrijving</span><span className="text-right">Debet</span><span className="text-right">Credit</span>
                      </div>
                      {detail.lines.map((l, i) => (
                        <div key={i} className="grid grid-cols-[70px_1fr_110px_110px] gap-2 px-3 py-1.5 text-sm border-t items-center">
                          <span className="font-mono text-xs">{l.account}</span>
                          <span>{l.name}</span>
                          <span className="text-right tabular-nums">{l.debet ? eur(l.debet) : ""}</span>
                          <span className="text-right tabular-nums">{l.credit ? eur(l.credit) : ""}</span>
                        </div>
                      ))}
                      <div className="grid grid-cols-[70px_1fr_110px_110px] gap-2 px-3 py-1.5 text-sm font-semibold border-t bg-muted/30">
                        <span></span><span>Totaal</span>
                        <span className="text-right tabular-nums">{eur(detail.total_debet)}</span>
                        <span className="text-right tabular-nums">{eur(detail.total_credit)}</span>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center justify-between gap-2 pt-1">
                  <div className="flex items-center gap-2">
                    {detail.source === "factuurboek" && (
                      <Button variant="outline" size="sm" data-testid="detail-open-fb" onClick={() => { window.location.href = "/factuurboek"; }}>
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Openen in Factuurboek
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50" data-testid="detail-delete" onClick={deleteEntry}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Verwijderen
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setDetail(null)} data-testid="detail-close">Sluiten</Button>
                    {detail.status !== "confirmed" && detail.confirmable && (
                      <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busyId === detail.id} data-testid="detail-confirm" onClick={confirmFromDetail}>
                        {busyId === detail.id ? "Bezig…" : "Bevestigen"}
                      </Button>
                    )}
                    {detail.status === "confirmed" && <Badge className="bg-emerald-600 gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Bevestigd</Badge>}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
